import { keyframes } from '@emotion/react'
import React, { type ComponentProps, type ReactNode, useContext } from 'react'
import { Transition } from 'react-transition-group'

import { CssVars } from '../../themes'
import { ToastContext } from './ToastContext'
import type { HorizontalAlignment, Variant } from './toastTypes'

const SNACKBAR_WIDTH = 756
const SIDE_ALIGNED_SNACKBAR_WIDTH = 375
export const TOAST_HEIGHT = '56px'

function getWidth(alignment?: HorizontalAlignment) {
  return alignment === 'center' || alignment === undefined
    ? SNACKBAR_WIDTH
    : SIDE_ALIGNED_SNACKBAR_WIDTH
}

function getGutterByAlignment(alignment?: HorizontalAlignment) {
  if (alignment === 'left') {
    return {
      marginLeft: CssVars.size.gutterWidth,
    }
  }

  if (alignment === 'right') {
    return {
      marginRight: CssVars.size.gutterWidth,
    }
  }

  return {
    margin: `0 ${CssVars.size.gutterWidth}`,
  }
}

function getJustifyPositionByAlignment(alignment?: HorizontalAlignment) {
  if (alignment === 'left') {
    return 'flex-start'
  }

  if (alignment === 'right') {
    return 'flex-end'
  }

  return 'center'
}

export function ToastTransition({
  children,
  index,
  numToasts,
  variant,
  ...props
}: {
  children: ReactNode
  index: number
  numToasts: number
  variant: Variant
} & Omit<ComponentProps<typeof Transition>, 'timeout' | 'children'>) {
  const context = useContext(ToastContext)

  if (!context) {
    throw new Error('<ToastTransition> must be wrapped inside <ToastProvider>')
  }

  const distanceFromEdge = `calc(${CssVars.size.gutterWidth} + ${
    numToasts - index - 1
  } * (${TOAST_HEIGHT} + ${CssVars.size.gutterWidth}))`

  return (
    <Transition timeout={500} {...props}>
      {state => {
        const isEntranceState = state === 'entering' || state === 'entered'
        const isExitState = state === 'exiting' || state === 'exited'

        return (
          <div
            css={{
              display: 'flex',
              justifyContent: getJustifyPositionByAlignment(
                context.horizontalAlignment
              ),
              [context.verticalAlignment]: 0,
              left: 0,
              position: 'fixed',
              width: '100%',
              zIndex: CssVars.size._experimentalToastZIndex as any,
            }}
          >
            <div
              css={[
                {
                  '> *': {
                    flexGrow: 1,
                  },
                  animationFillMode: 'both',
                  [context.verticalAlignment]: distanceFromEdge,
                  display: 'flex',
                  maxWidth: getWidth(context.horizontalAlignment),
                  position: 'absolute',
                  transition: `${context.verticalAlignment} 300ms ease-out`,

                  width: `calc(100% - 2 * ${CssVars.size.gutterWidth})`,
                },
                getGutterByAlignment(context.horizontalAlignment),
                isEntranceState && {
                  animationDuration: '300ms',
                  animationName: keyframes`
                      0% {
                        transform: translate3d(0, ${
                          TOAST_HEIGHT + CssVars.size.gutterWidth
                        }px, 0);
                        opacity: 0;
                      }
                      50% {
                        opacity: 0;
                      }
                      100% {
                        transform: translate3d(0, 0, 0);
                        opacity: 1;
                      }
                    `.toString(),
                  animationTimingFunction: 'ease-out',
                },
                isExitState && {
                  animationDuration: '200ms',
                  animationName: keyframes`
                      from { opacity: 1; }
                      to { opacity: 0; }
                    `.toString(),
                  pointerEvents: 'none',
                },
              ]}
            >
              {children}
            </div>
          </div>
        )
      }}
    </Transition>
  )
}
